import React from "react";

import { useQuery } from "@apollo/client";
import { Box, Typography, useTheme } from "@mui/material";
import { endOfWeek, format, getDate, startOfWeek } from "date-fns";
import { makeStyles } from "tss-react/mui";
import { VictoryAxis, VictoryBar, VictoryChart } from "victory";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()((theme) => ({
  waterIntakeChart: {
    display: "flex",
    flexDirection: "column",
    width: "504px",
    backgroundColor: colors.white,
    marginLeft: "12px",
    marginTop: "18px",
    minHeight: "95%",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: "36px",
    paddingLeft: "18px",
    paddingRight: "18px",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginLeft: 0,
      paddingLeft: "8px",
      paddingRight: "8px",
    },
  },
  waterIntakeChartHeader: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: "18px",
    paddingLeft: "18px",
  },
  goal: {
    display: "flex",
    backgroundColor: "#effbff",
    flexDirection: "row",
    borderWidth: "3px",
    borderRadius: "30px",
    alignItems: "center",
    justifyContent: "center",
    width: "108px",
    height: "38px",
    marginLeft: "8px",
  },
  goalLabel: {
    marginTop: "4px",
    fontSize: "18px",
  },
  goalText: {
    fontSize: "14px",
    color: "#62D9FF",
  },
  wrapper: {
    display: "flex",
    flexDirection: "row",
  },
  date: {
    fontSize: "18px",
    fontWeight: "bold",
  },
}));

type Props = {
  clientId: string;
  date: string;
  goal: Maybe<number> | undefined;
};

const waterIntakeBarGraphQuery = graphql(`
  query WaterIntakeBarGraph($date: ISO8601Date!, $clientId: ID) {
    waterIntakeByWeek(date: $date, clientId: $clientId) {
      totalAmountMl
      drankOn
    }
  }
`);

const WaterIntakeBarGraph: React.FC<Props> = ({ clientId, date, goal }) => {
  const { data } = useQuery(waterIntakeBarGraphQuery, {
    variables: { clientId: clientId, date: date },
  });
  const graphData =
    data?.waterIntakeByWeek?.map((log) => {
      return { date: log.drankOn, amount: log.totalAmountMl };
    }) ?? [];
  const theme = useTheme();
  const { classes: styles } = useStyles();
  const graphDates = data?.waterIntakeByWeek?.map((log) => log.drankOn) ?? [];
  const startWeek = format(startOfWeek(new Date(date)), "d MMM");
  const endWeek = format(endOfWeek(new Date(date)), "d MMM");
  const _goal = goal ? `${goal} ml` : "--";
  return (
    <>
      <Box className={styles.waterIntakeChart}>
        <Box className={styles.waterIntakeChartHeader}>
          <Typography variant="subtitle1" className={styles.date}>
            {startWeek} - {endWeek}
          </Typography>
          {Features.waterIntakeGoalEnabled && (
            <div className={styles.wrapper}>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                className={styles.goalLabel}
              >
                Goal
              </Typography>
              <Box className={styles.goal}>
                <Typography className={styles.goalText}>{_goal}</Typography>
              </Box>
            </div>
          )}
        </Box>

        <VictoryChart domainPadding={20}>
          <VictoryBar
            style={{
              data: { fill: "#62D9FF" },
              labels: { color: colors.gray },
            }}
            data={graphData}
            labels={({ datum }) => datum.amount}
            x="date"
            y="amount"
            cornerRadius={4}
            barWidth={8}
          />
          <VictoryAxis
            dependentAxis
            orientation="left"
            style={{
              axis: { stroke: "transparent" },
              grid: { stroke: theme.palette.grey["300"], strokeDasharray: 8 },
              ticks: { stroke: "none" },
            }}
          />
          <VictoryAxis
            standalone
            label="Dates"
            tickFormat={graphDates}
            style={{
              axis: { stroke: theme.palette.grey["300"] },
              grid: { stroke: "none" },
              axisLabel: {
                fontSize: "1.6rem",
                fontWeight: "bold",
                padding: 28,
                opacity: 0.6,
              },
            }}
          />
        </VictoryChart>
      </Box>
    </>
  );
};

export default WaterIntakeBarGraph;
